import type React from 'react';
import { OPERATIONS } from '@/pages/operations/operations';
import {
  AppstoreOutlined,
  ApiOutlined,
  BarChartOutlined,
  CloudUploadOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  ExportOutlined,
  FileTextOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  MessageOutlined,
  PayCircleOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
type NavigationItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  detailLabel?: string;
};

type NavigationGroup = {
  key: string;
  label: string;
  items: NavigationItem[];
};

export const navigationGroups: NavigationGroup[] = [
  {
    key: 'operations',
    label: '运营总览',
    items: [
      { key: '/dashboard', icon: <DashboardOutlined />, label: '运营驾驶舱' },
      { key: '/operations', icon: <DatabaseOutlined />, label: '业务运行中心' },
      { key: '/directory', icon: <AppstoreOutlined />, label: '全部功能' },
      { key: '/analytics', icon: <BarChartOutlined />, label: '渠道与流量' },
      { key: '/care-observatory', icon: <HeartOutlined />, label: '关爱观察站' },
    ],
  },
  {
    key: 'users',
    label: '用户与猫咪',
    items: [
      { key: '/users', icon: <TeamOutlined />, label: '用户管理', detailLabel: '用户详情' },
      { key: '/pets', icon: <HeartOutlined />, label: '猫咪档案', detailLabel: '猫咪详情' },
      { key: '/record-exports', icon: <ExportOutlined />, label: '记录导出测试' },
    ],
  },
  {
    key: 'content',
    label: '内容与审核',
    items: [
      { key: '/foods', icon: <ShoppingOutlined />, label: '食品工作台' },
      { key: '/treatments', icon: <MedicineBoxOutlined />, label: '药物与保健品' },
      {
        key: '/treatment-submissions',
        icon: <MessageOutlined />,
        label: '药物补报',
        detailLabel: '药物补报详情',
      },
      { key: '/feedback', icon: <MessageOutlined />, label: '意见反馈', detailLabel: '反馈详情' },
      { key: '/nutrition-profiles', icon: <DatabaseOutlined />, label: '营养参考值' },
    ],
  },
  {
    key: 'health-ai',
    label: '健康与 AI',
    items: [
      { key: '/checkup-reports', icon: <ExperimentOutlined />, label: '体检报告' },
      { key: '/visit-reports', icon: <FileTextOutlined />, label: '接诊摘要验证' },
      { key: '/lab-settings', icon: <ExperimentOutlined />, label: '体检 AI 提取' },
      { key: '/checkup-diagnosis-settings', icon: <MedicineBoxOutlined />, label: '体检 AI 诊疗' },
      { key: '/care-analysis-settings', icon: <HeartOutlined />, label: '护理 AI 分析' },
      { key: '/food-extraction-settings', icon: <ShoppingOutlined />, label: '食物成分提取' },
      { key: '/llm-models', icon: <DatabaseOutlined />, label: 'LLM 模型' },
      { key: '/llm-calls', icon: <ApiOutlined />, label: 'LLM 调用' },
    ],
  },
  {
    key: 'system',
    label: '系统运维',
    items: [
      {
        key: '/mini-program-feature-settings',
        icon: <PayCircleOutlined />,
        label: '小程序功能配置',
      },
      { key: '/storage-test', icon: <CloudUploadOutlined />, label: '存储测试' },
    ],
  },
];

const descriptions: Record<string, string> = {
  '/dashboard': '系统存量、每日变化、审核待办与渠道趋势',
  '/operations': '订单、订阅、AI 任务、提醒、照护成员与账号冲突',
  '/directory': '按业务分类浏览、搜索所有管理工具',
  '/analytics': 'iOS、微信小程序、网页的流量和接口表现',
  '/care-observatory': '照护热力图、记录趋势与用户参与',
  '/users': '账号资料、关联猫咪、记录与行为分析',
  '/pets': '健康档案、共同照护成员与完整记录',
  '/record-exports': '核对 PDF、长图等记录导出效果',
  '/foods': '食品目录、营养数据、证据、补报与报错',
  '/treatments': '药物、保健品、补液的资料与图片维护',
  '/treatment-submissions': '处理用户补充的药物资料与回复',
  '/feedback': '阅读、定位并回复用户反馈',
  '/nutrition-profiles': '维护营养参考标准与适用条件',
  '/checkup-reports': '核对提取结果、异常指标与报告来源',
  '/visit-reports': '验证接诊摘要内容与生成效果',
  '/lab-settings': '配置体检提取提示词、模型和测试',
  '/checkup-diagnosis-settings': '配置体检解读模型、提示词和测试',
  '/care-analysis-settings': '护理分析开关、模型与提示词',
  '/food-extraction-settings': '配置食品营养和配料提取',
  '/llm-models': '管理模型供应商、当前模型与价格',
  '/llm-calls': '追踪模型调用、延迟、Token 和费用',
  '/mini-program-feature-settings': '会员入口、配额、付费与功能开关',
  '/storage-test': '验证图片上传和对象存储访问',
};

export const managementEntries = [
  ...navigationGroups.flatMap((group) =>
    group.items.map((item) => ({
      ...item,
      group: group.label,
      description: descriptions[item.key] ?? item.label,
    })),
  ),
  ...Object.entries(OPERATIONS).map(([key, item]) => ({
    key: `/operations?tab=${key}`,
    label: item.label,
    icon: <DatabaseOutlined />,
    group: '业务运行中心',
    description: item.description,
  })),
  {
    key: '/foods?tab=submissions&submissionStatus=awaiting_admin',
    label: '待审核食物补报',
    icon: <ShoppingOutlined />,
    group: '内容与审核',
    description: '查看等待管理员处理的食品补充资料',
  },
  {
    key: '/foods?tab=reports&reportStatus=pending',
    label: '待处理食品报错',
    icon: <ShoppingOutlined />,
    group: '内容与审核',
    description: '核对用户报告的营养数据和资料问题',
  },
  {
    key: '/foods?tab=approvals',
    label: '食品变更确认',
    icon: <ShoppingOutlined />,
    group: '内容与审核',
    description: '跟进等待用户确认的食品修改',
  },
];

export function searchManagementEntries(query: string) {
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return managementEntries
    .filter((item) =>
      terms.every((term) =>
        `${item.label} ${item.group} ${item.description} ${item.key}`
          .toLocaleLowerCase()
          .includes(term),
      ),
    )
    .sort((a, b) => {
      const score = (item: (typeof managementEntries)[number]) =>
        terms.reduce(
          (sum, term) => sum + (item.label.toLocaleLowerCase().includes(term) ? 10 : 0),
          0,
        );
      return score(b) - score(a);
    });
}
